import { EnableDisableEnum } from '@app/app.enum';
import store from '@app/app.store';
import * as models from '@app/models';
import { IAccountAppResponseVM, ICarrierDetails } from '@app/models';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { IntlService } from '@app/services/intl.service';
import { Grid, InputBase, MenuItem, TextField } from '@material-ui/core';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { getCarrierDetailsSchema } from './carriers.schema';

const accountService: AccountService = Injector.get(AccountService);
const intlService: IntlService = Injector.get(IntlService);

const CarrierDetailsComponent: React.FunctionComponent<models.ICarrierDetailsProps> = forwardRef(
	(props, ref: React.MutableRefObject<any>) => {
		const formRef: React.MutableRefObject<Formik> = useRef(null);
		const [appId, setAppId] = useState<string>(() =>
			props.disableAppDropDown ? '' : props.initialValues.appId || accountService.getUserData().apps[0].appId
		);
		const [apps, updateApps] = useState<models.IAccountAppResponseVM[]>([]);

		useEffect(() => {
			async function fetchAppById() {
				try {
					const result = await accountService.getAppById(props.initialValues.appId);
					updateApps([result]);
				} catch (error) {
					throw error;
				}
			}

			if (accountService.IsAdmin(store.getState().authentication.user.role) && !!props.initialValues.appId) {
				fetchAppById();
			} else {
				updateApps(accountService.getUserData().apps);
			}
		}, []);

		let formValues: ICarrierDetails = null;

		useImperativeHandle(ref, () => ({
			getFormValues: async () => {
				try {
					await formRef.current.submitForm();
					const errors = Object.keys(formRef.current.state.errors).length;
					if (!errors) {
						return formValues;
					}
					return null;
				} catch (err) {
					throw err;
				}
			},
		}));

		return (
			<Grid container={true} justifyContent='center' alignItems='center'>
				<Grid item={true} sm={12} md={12} lg={12} xl={12}>
					<Formik
						enableReinitialize={true}
						validationSchema={getCarrierDetailsSchema(!!props.initialValues.appId || props.disableAppDropDown)}
						initialValues={{ ...props.initialValues, appId }}
						onSubmit={() => {}}
						ref={formRef}
					>
						{(formikProps: FormikProps<FormikValues>) => {
							// Set form values to an outside variable
							formValues = formikProps.values as ICarrierDetails;

							return (
								<Form noValidate={true} className='width-100'>
									<Field
										variant='outlined'
										name='carrierDescription'
										readOnly={true}
										fullWidth={true}
										margin='dense'
										value={formikProps.values.carrierDescription}
										component={InputBase}
										error={!!formikProps.errors.carrierDescription}
										onChange={formikProps.handleChange('carrierDescription')}
										multiline={true}
										rowsMax={3}
										rows={3}
									/>
									<Grid container={true} spacing={2}>
										<Grid item={true} xs={6}>
											{props.isCreatingNewApp ? (
												<Field
													fullWidth={true}
													variant='outlined'
													size='small'
													label={intlService.GetFormattedMessage('app.carrier-details.app-label')}
													name='app-name'
													disabled={true}
													value={formikProps.values.appName}
													component={TextField}
												/>
											) : (
												<Field
													name='appId'
													render={() => (
														<>
															<TextField
																variant='outlined'
																size='small'
																select={true}
																fullWidth={true}
																disabled={!!props.initialValues.appId || props.disableAppDropDown}
																value={formikProps.values.appId}
																label={intlService.GetFormattedMessage('app.carrier-details.app-label')}
																onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
																	formikProps.handleChange('appId')(e);
																	setAppId(e.target.value);
																}}
																SelectProps={{
																	renderValue: (id: string) => apps.find((e) => e.appId === id)?.appName,
																	MenuProps: {
																		PaperProps: {
																			style: {
																				maxHeight: '250px',
																			},
																		},
																	},
																}}
																error={
																	!!(
																		(formikProps.values.appId.length || formikProps.submitCount) &&
																		!!formikProps.errors.appId
																	)
																}
																helperText={
																	formikProps.values.appId.length || formikProps.submitCount
																		? formikProps.errors.appId
																		: null
																}
															>
																{apps
																	.filter((x) => x.status === EnableDisableEnum.Enabled)
																	.map((app: IAccountAppResponseVM, index: number) => (
																		<MenuItem key={index} value={app.appId}>
																			{app.appName}
																		</MenuItem>
																	))}
															</TextField>
														</>
													)}
												/>
											)}
										</Grid>
										<Grid item={true} xs={6}>
											<Field
												name='name'
												size='small'
												label={props.intl.formatMessage({
													id: 'app.account.name-label',
												})}
												variant='outlined'
												component={TextField}
												type='text'
												fullWidth={true}
												required={true}
												value={formikProps.values.name}
												onChange={(e: React.ChangeEvent) => formikProps.handleChange('name')(e)}
												error={
													!!((formikProps.values.name.length || formikProps.submitCount) && !!formikProps.errors.name)
												}
												helperText={
													formikProps.values.name.length || formikProps.submitCount ? formikProps.errors.name : null
												}
											/>
										</Grid>
									</Grid>
									<Grid container={true} spacing={2}>
										<Grid item={true} xs={12}>
											<Field
												name='description'
												size='small'
												label={props.intl.formatMessage({
													id: 'app.account.description-label',
												})}
												multiline={true}
												rowsMax={6}
												rows={3}
												variant='outlined'
												component={TextField}
												type='textarea'
												fullWidth={true}
												required={true}
												value={formikProps.values.description}
												onChange={(e: React.ChangeEvent) => formikProps.handleChange('description')(e)}
												error={
													!!(
														(formikProps.values.description.length || formikProps.submitCount) &&
														!!formikProps.errors.description
													)
												}
												helperText={
													formikProps.values.description.length || formikProps.submitCount
														? formikProps.errors.description
														: null
												}
											/>
										</Grid>
									</Grid>
								</Form>
							);
						}}
					</Formik>
				</Grid>
			</Grid>
		);
	}
);

export default injectIntl(CarrierDetailsComponent, { withRef: true });



// WEBPACK FOOTER //
// ./src/components/carriers/carrier-details.component.tsx